/**
 * mapProperties - Maps property names in nodes and/or links
 *
 * Useful when data uses different property names than netviz expects
 * (e.g., 'name' instead of 'id', 'weight' instead of 'value').
 *
 * @param {Object} network - { nodes, links } network data
 * @param {Object} options - Mapping configuration
 * @param {Object} [options.nodes] - Node property mappings { to: from } or { to: accessor }
 * @param {Object} [options.links] - Link property mappings { to: from } or { to: accessor }
 * @returns {Object} { nodes, links } with mapped properties
 *
 * @example
 * // Simple property rename (value is the source property name)
 * const mapped = mapProperties(data, {
 *   nodes: { id: 'name', group: 'country' }
 * });
 *
 * @example
 * // With accessor functions
 * const mapped = mapProperties(data, {
 *   nodes: {
 *     id: d => d.name,
 *     label: d => d.fullName || d.name,
 *     group: d => d.category
 *   },
 *   links: {
 *     value: d => d.weight || 1
 *   }
 * });
 */
export function mapProperties(
  network,
  { nodes: nodeMapping = {}, links: linkMapping = {} } = {}
) {
  if (!network || !network.nodes) {
    throw new Error("mapProperties: network must have a nodes array");
  }

  function applyMapping(item, mapping) {
    const result = { ...item };
    for (const [to, from] of Object.entries(mapping)) {
      if (typeof from === "function") {
        result[to] = from(item);
      } else if (typeof from === "string" && from in item) {
        result[to] = item[from];
        // Remove old property if renamed (but not if mapping to same name)
        if (to !== from) delete result[from];
      }
    }
    return result;
  }

  return {
    nodes: network.nodes.map((n) => applyMapping(n, nodeMapping)),
    links: (network.links || []).map((l) => applyMapping(l, linkMapping)),
  };
}
